<template>
  <form @submit.prevent="handleSubmit" class="enrollment-form">
    <div class="form-field">
      <IntercomSelect
        v-model="formData.program_id"
        :label="$t('enrollment.program')"
        :options="programOptions"
        :disabled="loading"
        filterable
        required
      />
    </div>

    <div class="form-field">
      <IntercomDateInput
        v-model="formData.enrolled_date"
        :label="$t('enrollment.enrolledDate')"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomSelect
        v-model="formData.status"
        :label="$t('enrollment.status')"
        :options="statusOptions"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.notes"
        :label="$t('enrollment.notes')"
        placeholder="Optional notes"
        :disabled="loading"
      />
    </div>

    <div class="form-actions">
      <IntercomButton type="button" variant="ghost" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton type="submit" variant="primary" :loading="loading">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  IntercomInput,
  IntercomDateInput,
  IntercomSelect,
  IntercomButton,
} from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  enrollment: { type: Object, default: null },
  programs: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel'])

const formData = reactive({
  program_id: '',
  enrolled_date: dayjs().format('YYYY-MM-DD'),
  status: 'active',
  notes: '',
})

const programOptions = computed(() =>
  props.programs.map(p => ({ value: p.id, label: p.name }))
)

const statusOptions = computed(() => [
  { value: 'active', label: t('enrollment.statuses.active') },
  { value: 'completed', label: t('enrollment.statuses.completed') },
  { value: 'withdrawn', label: t('enrollment.statuses.withdrawn') },
  { value: 'waitlisted', label: t('enrollment.statuses.waitlisted') },
])

watch(() => props.enrollment, (newEnrollment) => {
  if (newEnrollment) {
    formData.program_id = newEnrollment.program_id || ''
    formData.enrolled_date = newEnrollment.enrolled_date || dayjs().format('YYYY-MM-DD')
    formData.status = newEnrollment.status || 'active'
    formData.notes = newEnrollment.notes || ''
  }
}, { immediate: true })

const handleSubmit = () => {
  const data = { ...formData }
  if (!data.notes) delete data.notes
  emit('submit', data)
}
</script>

<style scoped>
.enrollment-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}
</style>
